<template>
  <el-dialog
    :title="`Agent 账单 - ${agentName || ''}`"
    :visible="visible"
    fullscreen
    custom-class="billing-fullscreen-dialog"
    :close-on-click-modal="false"
    @update:visible="onClose"
    @closed="onClosed"
  >
    <!-- Period tabs -->
    <el-tabs v-model="activePeriod" @tab-click="onPeriodChange">
      <el-tab-pane label="年度" name="annual" />
      <el-tab-pane label="月度" name="monthly" />
    </el-tabs>

    <!-- Summary cards -->
    <section class="billing-summary-cards">
      <div class="billing-summary-card">
        <div class="billing-card-label">总 Token</div>
        <div class="billing-card-value">{{ $formatNumber(summary.total_tokens) }}</div>
      </div>
      <div class="billing-summary-card">
        <div class="billing-card-label">费用</div>
        <div class="billing-card-value" style="color:#34c98f;">¥ {{ $formatNumber(summary.total_cost) }}</div>
      </div>
      <div class="billing-summary-card">
        <div class="billing-card-label">本月消费</div>
        <div class="billing-card-value" style="color:#34c98f;">¥ {{ $formatNumber(summary.month_cost) }}</div>
      </div>
      <div class="billing-summary-card">
        <div class="billing-card-label">月上限</div>
        <div class="billing-card-value" :class="summary.monthly_budget > 0 ? 'billing-value-green' : 'billing-value-muted'">
          {{ summary.monthly_budget > 0 ? '¥ ' + $formatNumber(summary.monthly_budget) : '无上限' }}
        </div>
      </div>
      <div class="billing-summary-card">
        <div class="billing-card-label">调用次数</div>
        <div class="billing-card-value">{{ $formatNumber(summary.total_calls) }}</div>
      </div>
    </section>

    <!-- Trend chart -->
    <section class="billing-chart-panel" v-if="trendData.length">
      <div class="billing-panel-head">
        <h3 class="billing-panel-title">消费趋势</h3>
        <span class="billing-panel-sub">{{ rangeLabel }}</span>
      </div>
      <div ref="trendChart" class="billing-chart-container" />
    </section>

    <!-- Items table -->
    <section class="billing-table-panel">
      <div class="billing-toolbar">
        <span class="billing-toolbar-title">{{ periodTitle }}</span>
        <el-date-picker
          v-if="activePeriod === 'annual'"
          v-model="pickerYear"
          type="year"
          placeholder="选择年份"
          size="small"
          value-format="yyyy"
          class="billing-date-picker"
          @change="onDateChange"
        />
        <el-date-picker
          v-else
          v-model="pickerMonth"
          type="month"
          placeholder="选择月份"
          size="small"
          value-format="yyyy-MM"
          class="billing-date-picker"
          @change="onDateChange"
        />
        <el-button size="small" icon="el-icon-download" @click="onExport">导出</el-button>
      </div>
      <el-table :data="items" size="small" style="width: 100%" v-loading="loading">
        <el-table-column type="index" label="序号" width="60" :index="pageIndex" />
        <el-table-column label="周期">
          <template slot-scope="scope">
            <el-button v-if="scope.row.year" type="text" size="small" @click="onDrillDown(scope.row)">
              {{ scope.row.label }}
            </el-button>
            <span v-else>{{ scope.row.label }}</span>
          </template>
        </el-table-column>
        <el-table-column label="总 Token" align="right" min-width="110">
          <template slot-scope="scope">{{ $formatNumber(scope.row.total_tokens) }}</template>
        </el-table-column>
        <el-table-column label="调用次数" align="right" min-width="90">
          <template slot-scope="scope">{{ scope.row.total_calls }}</template>
        </el-table-column>
        <el-table-column label="费用" align="right" min-width="110">
          <template slot-scope="scope">¥ {{ $formatNumber(scope.row.total_cost) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />
    </section>
  </el-dialog>
</template>

<script>
import * as echarts from 'echarts'
import * as XLSX from 'xlsx'

export default {
  name: 'AgentBillsDialog',
  props: {
    visible: { type: Boolean, default: false },
    agentId: { type: [Number, String], default: null },
    agentName: { type: String, default: '' }
  },
  data() {
    const now = new Date()
    return {
      activePeriod: 'annual',
      pickerYear: String(now.getFullYear()),
      pickerMonth: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0'),
      summary: {
        totalTokens: 0,
        totalCost: 0,
        totalCalls: 0,
        monthCost: 0,
        monthlyBudget: 0
      },
      trendData: [],
      items: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      loading: false,
      chart: null
    }
  },
  computed: {
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    periodTitle() {
      const map = { monthly: '月度账单', annual: '年度账单' }
      return map[this.activePeriod] || '账单'
    },
    rangeLabel() {
      return this.activePeriod === 'annual' ? this.pickerYear + '年' : this.pickerMonth
    },
    billingBase() {
      return `/admin/agents/${this.agentId}/billing`
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.currentPage = 1
        this.fetchAll()
      }
    }
  },
  beforeDestroy() {
    if (this.chart) { this.chart.dispose(); this.chart = null }
  },
  methods: {
    onClose() {
      this.$emit('update:visible', false)
    },
    onClosed() {
      if (this.chart) { this.chart.dispose(); this.chart = null }
    },
    async fetchAll() {
      this.loading = true
      try {
        const params = this.buildParams()
        const [summary, trend] = await Promise.all([
          this.$http.get(this.billingBase + '/summary', { params }),
          this.$http.get(this.billingBase + '/trend', { params: { period: params.period, year: params.year, month: params.month } })
        ])
        this.summary = summary
        this.trendData = trend || []
        await this.fetchItems()
        this.$nextTick(() => this.renderChart())
      } catch {
        this.$message.error('加载账单数据失败')
      } finally {
        this.loading = false
      }
    },
    async fetchItems() {
      try {
        const params = { ...this.buildParams(), page: this.currentPage, pageSize: this.pageSize }
        const res = await this.$http.get(this.billingBase + '/items', { params })
        this.items = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载账单明细失败')
      }
    },
    buildParams() {
      const params = { period: this.activePeriod }
      if (this.activePeriod === 'annual') {
        params.year = parseInt(this.pickerYear) || new Date().getFullYear()
      } else {
        const parts = (this.pickerMonth || '').split('-')
        params.year = parseInt(parts[0]) || new Date().getFullYear()
        params.month = parseInt(parts[1]) || (new Date().getMonth() + 1)
      }
      return params
    },
    onPeriodChange() {
      this.currentPage = 1
      this.fetchAll()
    },
    onDateChange() {
      this.currentPage = 1
      this.fetchAll()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchItems()
    },
    onDrillDown(row) {
      if (this.activePeriod === 'annual' && row.year && row.month) {
        this.activePeriod = 'monthly'
        this.pickerMonth = row.year + '-' + String(row.month).padStart(2, '0')
        this.fetchAll()
      }
    },
    onExport() {
      const params = this.buildParams()
      this.$http.get(this.billingBase + '/export', { params }).then(records => {
        if (!records || !records.length) {
          this.$message.info('没有数据可导出')
          return
        }
        const data = records.map(r => ({
          '周期': r.label,
          '总Token': r.total_tokens,
          '调用次数': r.total_calls,
          '费用': r.total_cost
        }))
        const ws = XLSX.utils.json_to_sheet(data)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, this.periodTitle)
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([wbout], { type: 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const ts = new Date()
        const pad = n => String(n).padStart(2, '0')
        a.download = `${this.periodTitle}_${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      }).catch(() => {
        this.$message.error('导出失败')
      })
    },
    renderChart() {
      const el = this.$refs.trendChart
      if (!el || !this.trendData.length) {
        if (this.chart) { this.chart.dispose(); this.chart = null }
        return
      }
      if (this.chart) this.chart.dispose()
      const chart = echarts.init(el)
      this.chart = chart

      const dates = this.trendData.map(d => this.formatLabel(d.date))
      const costs = this.trendData.map(d => Number(d.cost) || 0)

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(20,25,40,0.9)',
          borderColor: 'rgba(133,164,255,0.2)',
          textStyle: { color: '#c8d0e0', fontSize: 12 }
        },
        grid: { left: 60, right: 20, top: 20, bottom: 30 },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: 'rgba(133,164,255,0.15)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11, rotate: dates.length > 24 ? 45 : 0 }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: 'rgba(133,164,255,0.08)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11, formatter: v => '¥' + v }
        },
        series: [{
          type: 'line',
          data: costs,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color: '#34c98f' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(52,201,143,0.28)' },
              { offset: 1, color: 'rgba(52,201,143,0)' }
            ])
          }
        }]
      })
    },
    formatLabel(date) {
      if (!date) return date
      const parts = date.split('-')
      if (this.activePeriod === 'annual') return parseInt(parts[1]) + '月'
      return parseInt(parts[2]) + '日'
    }
  }
}
</script>

<style scoped>
/* 账单弹窗样式（对齐客户账单弹窗） */
::v-deep .billing-fullscreen-dialog .el-dialog__body {
  padding: 20px 40px;
}

.billing-summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.billing-summary-card {
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}

.billing-card-label {
  font-size: 11px;
  color: var(--muted);
  margin-bottom: 6px;
  font-weight: 700;
}

.billing-card-value {
  font-size: 20px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.1;
}

.billing-value-green { color: #34c98f; }
.billing-value-muted { color: var(--muted-2); }

.billing-chart-panel {
  padding: 16px;
  margin-bottom: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
}

.billing-chart-container {
  width: 100%;
  height: 220px;
}

.billing-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.billing-panel-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
}

.billing-panel-sub {
  color: var(--muted);
  font-size: 12px;
}

.billing-table-panel {
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
}

.billing-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.billing-toolbar-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  flex: 1;
}

.billing-date-picker {
  width: 140px;
}

.billing-table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 700;
}

.billing-table-panel ::v-deep td,
.billing-table-panel ::v-deep th {
  border-color: var(--line-soft);
}

::v-deep .billing-fullscreen-dialog .el-tabs__header {
  border-bottom: 1px solid rgba(133, 164, 255, 0.15);
  margin-bottom: 20px;
}

::v-deep .billing-fullscreen-dialog .el-tabs__item {
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-weight: 800;
  height: 44px;
  line-height: 44px;
  letter-spacing: 0.02em;
}

::v-deep .billing-fullscreen-dialog .el-tabs__item:hover {
  color: var(--text);
}

::v-deep .billing-fullscreen-dialog .el-tabs__item.is-active {
  color: var(--text);
}

::v-deep .billing-fullscreen-dialog .el-tabs__active-bar {
  background: #55f0b3;
  height: 2.5px;
  border-radius: 2px;
}

::v-deep .billing-fullscreen-dialog .el-tabs__nav-wrap::after {
  background: transparent;
}

.pagination-bar {
  margin-top: 16px;
  text-align: right;
}
</style>